import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";
import { BarChart3, Users, Calendar, Layers, Truck, CheckCircle2, XCircle, RotateCw } from "lucide-react";

const isoDaysAgo = (n) => {
  const d = new Date(Date.now() - n * 86400000);
  return d.toISOString().slice(0, 10);
};

export default function ReportsPage() {
  const [dateFrom, setDateFrom] = useState(isoDaysAgo(29));
  const [dateTo, setDateTo] = useState(isoDaysAgo(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/production", {
        params: { date_from: `${dateFrom}T00:00:00+00:00`, date_to: `${dateTo}T23:59:59+00:00` },
      });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setPreset = (days) => {
    setDateFrom(isoDaysAgo(days - 1));
    setDateTo(isoDaysAgo(0));
    setTimeout(load, 0);
  };

  const maxDay = useMemo(() => Math.max(1, ...(data?.by_day?.map(d => d.count) || [1])), [data]);
  const maxOp = useMemo(() => Math.max(1, ...(data?.by_operator?.map(d => d.count) || [1])), [data]);
  const maxCat = useMemo(() => Math.max(1, ...(data?.by_category?.map(d => d.count) || [1])), [data]);

  return (
    <div>
      <PageHeader
        title="Performans Raporu"
        subtitle="Üretim çıktısı, sevkiyat ve operatör performansı."
        actions={
          <div className="flex gap-2 flex-wrap items-center">
            <PresetBtn label="Bugün" onClick={() => setPreset(1)} testid="preset-1"/>
            <PresetBtn label="7 Gün" onClick={() => setPreset(7)} testid="preset-7"/>
            <PresetBtn label="30 Gün" onClick={() => setPreset(30)} testid="preset-30"/>
            <PresetBtn label="90 Gün" onClick={() => setPreset(90)} testid="preset-90"/>
          </div>
        }
      />

      <div className="card-industrial p-5 mb-6 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs uppercase font-bold text-slate-600 mb-1">Başlangıç</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            data-testid="report-date-from" className="px-3 py-2 border-2 border-slate-300 focus:border-blue-700 outline-none font-mono"/>
        </div>
        <div>
          <label className="block text-xs uppercase font-bold text-slate-600 mb-1">Bitiş</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            data-testid="report-date-to" className="px-3 py-2 border-2 border-slate-300 focus:border-blue-700 outline-none font-mono"/>
        </div>
        <button onClick={load} disabled={loading} data-testid="report-refresh-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase text-sm">
          <RotateCw size={16} className={loading ? "animate-spin" : ""}/> Uygula
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SumCard testid="sum-produced" label="Üretilen (Hazır)" value={data?.summary?.produced ?? "—"} tone="blue" icon={CheckCircle2}/>
        <SumCard testid="sum-shipped" label="Sevk Edilen" value={data?.summary?.shipped ?? "—"} tone="slate" icon={Truck}/>
        <SumCard testid="sum-qc-passed" label="QC Onay" value={data?.summary?.qc_passed ?? "—"} tone="emerald" icon={CheckCircle2}/>
        <SumCard testid="sum-qc-failed" label="QC Ret" value={data?.summary?.qc_failed ?? "—"} tone="rose" icon={XCircle}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Günlük Üretim" icon={Calendar}>
          {(!data?.by_day || data.by_day.length === 0) ? <Empty/> : (
            <div className="space-y-1.5 max-h-96 overflow-y-auto" data-testid="chart-by-day">
              {data.by_day.map(d => (
                <BarRow key={d.date} label={d.date} count={d.count} max={maxDay} tone="blue"/>
              ))}
            </div>
          )}
        </Card>

        <Card title="Operatör Performansı" icon={Users}>
          {(!data?.by_operator || data.by_operator.length === 0) ? <Empty/> : (
            <div className="space-y-1.5" data-testid="chart-by-operator">
              {data.by_operator.map(o => (
                <BarRow key={(o.user_id || o.user_name) + o.count} label={`${o.user_name} · ${o.user_role || ""}`} count={o.count} max={maxOp} tone="emerald"/>
              ))}
            </div>
          )}
        </Card>

        <Card title="Kategori Dağılımı" icon={Layers}>
          {(!data?.by_category || data.by_category.length === 0) ? <Empty/> : (
            <div className="space-y-1.5" data-testid="chart-by-category">
              {data.by_category.map(c => (
                <BarRow key={c.category} label={c.category} count={c.count} max={maxCat} tone="slate"/>
              ))}
            </div>
          )}
        </Card>

        <Card title="Özet" icon={BarChart3}>
          <div className="grid grid-cols-2 gap-3 text-sm" data-testid="report-summary-block">
            <Kv k="Aralık" v={`${data?.date_from || "—"} → ${data?.date_to || "—"}`} />
            <Kv k="Toplam Üretilen" v={data?.summary?.produced ?? "—"}/>
            <Kv k="Sevk Edilen" v={data?.summary?.shipped ?? "—"}/>
            <Kv k="QC Onay" v={data?.summary?.qc_passed ?? "—"}/>
            <Kv k="QC Ret" v={data?.summary?.qc_failed ?? "—"}/>
            <Kv k="Farklı Gün" v={data?.by_day?.length ?? 0}/>
            <Kv k="Aktif Operatör" v={data?.by_operator?.length ?? 0}/>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="card-industrial p-5">
      <h2 className="font-display font-black text-lg mb-4 flex items-center gap-2">{Icon && <Icon size={18}/>} {title}</h2>
      {children}
    </div>
  );
}
function Empty() { return <div className="text-sm text-slate-500 py-6 text-center">Bu aralıkta veri yok.</div>; }
function Kv({ k, v }) {
  return (
    <div className="border border-slate-200 p-2 bg-slate-50">
      <div className="text-[10px] uppercase text-slate-500 font-bold">{k}</div>
      <div className="font-mono font-bold text-slate-900 truncate" title={String(v)}>{v}</div>
    </div>
  );
}
function BarRow({ label, count, max, tone }) {
  const tones = { blue: "bg-blue-600", emerald: "bg-emerald-600", slate: "bg-slate-700", rose: "bg-rose-500" };
  const w = Math.max(4, Math.round((count / max) * 100));
  return (
    <div className="flex items-center gap-3 text-xs">
      <div className="w-40 truncate text-slate-700 font-semibold" title={label}>{label}</div>
      <div className="flex-1 bg-slate-100 h-4">
        <div className={`${tones[tone] || tones.blue} h-4`} style={{ width: `${w}%` }}/>
      </div>
      <div className="w-10 text-right tabular-nums font-bold">{count}</div>
    </div>
  );
}
function PresetBtn({ label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="px-3 py-1.5 border-2 border-slate-300 hover:border-slate-800 text-xs font-bold uppercase">
      {label}
    </button>
  );
}
function SumCard({ label, value, tone, icon: Icon, testid }) {
  const tones = {
    blue: "border-blue-700 text-blue-700",
    slate: "border-slate-800 text-slate-900",
    emerald: "border-emerald-500 text-emerald-700",
    rose: "border-rose-500 text-rose-700",
  };
  return (
    <div className={`border-2 ${tones[tone]} bg-white p-4`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase font-bold tracking-widest opacity-80">{label}</div>
        {Icon && <Icon size={18} className="opacity-60"/>}
      </div>
      <div className="font-display font-black text-3xl tabular-nums mt-1">{value}</div>
    </div>
  );
}
